/**
 * CustomizationsTab - Global Add-Ons & Modifier Library
 * 
 * Features:
 * 1. Add-Ons: Simple extras with a single price (e.g., Extra Shot $1.50)
 * 2. Modifier Groups: Selection groups with multiple options and prices
 *    - Example: "Milk Type" with options [Regular (free), Oat ($1.50), Almond ($1.50)]
 *    - Customers must select ONE option from the group
 *    - Assignable to menu categories (e.g., Coffee, Iced Drinks)
 */
import { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, X, Check, ChevronDown, ChevronUp, Layers, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/utils/apiClient';

const addOnCategories = [
  { value: 'general', label: 'General' },
  { value: 'espresso', label: 'Espresso/Coffee' },
  { value: 'protein', label: 'Protein' },
  { value: 'dairy', label: 'Dairy' },
  { value: 'sauce', label: 'Sauce' },
  { value: 'topping', label: 'Topping' },
  { value: 'vegetable', label: 'Vegetable' }
];

const menuCategories = [
  { value: 'coffee', label: 'Coffee' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'sweets', label: 'Sweets' },
  { value: 'iced-drinks', label: 'Iced Drinks' },
  { value: 'smoothies-blends', label: 'Smoothies & Blends' },
  { value: 'fresh-mixed-juice', label: 'Fresh Mixed Juice' },
  { value: 'milk-shakes', label: 'Milk Shakes' }
];

// Pre-defined modifier group templates using ACTUAL production pricing
// Based on Bulaccino's existing add-ons: milk upgrades = $2, not $1.50
const MODIFIER_TEMPLATES = [
  {
    name: 'Milk Type',
    options: [
      { name: 'Regular Milk', price: 0 },
      { name: 'Oat Milk', price: 2 },
      { name: 'Almond Milk', price: 2 },
      { name: 'Soy Milk', price: 2 },
      { name: 'Skim Milk', price: 0 },
    ],
    compatible_categories: ['coffee', 'iced-drinks', 'smoothies-blends', 'milk-shakes']
  },
  {
    name: 'Sugar Level',
    options: [
      { name: 'No Sugar', price: 0 },
      { name: 'Half Sugar', price: 0 },
      { name: 'Regular Sugar', price: 0 },
      { name: 'Extra Sugar', price: 0 },
    ],
    compatible_categories: ['coffee', 'iced-drinks']
  },
  {
    name: 'Temperature',
    options: [
      { name: 'Hot', price: 0 },
      { name: 'Iced', price: 0 },
      { name: 'Extra Hot', price: 0 },
    ],
    compatible_categories: ['coffee']
  },
  {
    name: 'Coffee Strength',
    options: [
      { name: 'Regular', price: 0 },
      { name: 'Extra Shot', price: 2 },
      { name: 'Decaf', price: 0 },
    ],
    compatible_categories: ['coffee', 'iced-drinks']
  },
];

export default function CustomizationsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('modifiers'); // 'modifiers' or 'addons'
  
  // Add-Ons State
  const [addOns, setAddOns] = useState([]);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newAddOn, setNewAddOn] = useState({
    name: '', price: '', category: 'general', compatible_categories: [], is_available: true
  });
  
  // Modifier Groups State
  const [modifierGroups, setModifierGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [newGroup, setNewGroup] = useState({
    name: '',
    options: [{ name: '', price: 0 }],
    compatible_categories: [],
    is_required: false,
    is_available: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      setSettings(res.data);
      setAddOns(res.data?.global_add_ons || []);
      setModifierGroups(res.data?.modifier_groups || []);
    } catch (err) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  // ============== MODIFIER GROUPS FUNCTIONS ==============
  
  const saveModifierGroups = async (groups = modifierGroups) => {
    setSaving(true);
    try {
      const cleanGroups = groups.map(group => ({
        id: group.id,
        name: group.name,
        options: (group.options || []).map(opt => ({
          name: opt.name,
          price: typeof opt.price === 'string' ? parseFloat(opt.price) || 0 : (opt.price || 0)
        })),
        compatible_categories: Array.isArray(group.compatible_categories) ? group.compatible_categories : [],
        is_required: group.is_required || false,
        is_available: group.is_available !== false
      }));
      
      const updatedSettings = {
        ...settings,
        modifier_groups: cleanGroups
      };
      
      await api.put('/admin/settings', updatedSettings);
      await fetchSettings();
      toast.success('Modifier groups saved!');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save modifier groups');
    }
    setSaving(false);
  };

  const addNewModifierGroup = () => {
    if (!newGroup.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    if (newGroup.options.filter(o => o.name.trim()).length === 0) {
      toast.error('Please add at least one option');
      return;
    }
    
    const group = {
      id: `mod-group-${Date.now()}`,
      name: newGroup.name.trim(),
      options: newGroup.options.filter(o => o.name.trim()).map(o => ({
        name: o.name.trim(),
        price: parseFloat(o.price) || 0
      })),
      compatible_categories: newGroup.compatible_categories,
      is_required: newGroup.is_required,
      is_available: true
    };
    
    const updated = [...modifierGroups, group];
    setModifierGroups(updated);
    saveModifierGroups(updated);
    
    setNewGroup({
      name: '',
      options: [{ name: '', price: 0 }],
      compatible_categories: [],
      is_required: false,
      is_available: true
    });
  };

  const addTemplateGroup = (template) => {
    // Check if group with same name already exists
    if (modifierGroups.some(g => g.name.toLowerCase() === template.name.toLowerCase())) {
      toast.error(`"${template.name}" group already exists`);
      return;
    }
    
    const group = {
      id: `mod-group-${Date.now()}`,
      name: template.name,
      options: template.options.map(o => ({ ...o })),
      compatible_categories: [...template.compatible_categories],
      is_required: false,
      is_available: true
    };
    
    const updated = [...modifierGroups, group];
    setModifierGroups(updated);
    saveModifierGroups(updated);
    toast.success(`Added "${template.name}" modifier group!`);
  };

  const removeModifierGroup = (id) => {
    const updated = modifierGroups.filter(g => g.id !== id);
    setModifierGroups(updated);
    saveModifierGroups(updated);
  };

  const toggleGroupExpand = (id) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const startEditGroup = (group) => {
    setEditingGroup({ ...group, options: [...group.options] });
    setExpandedGroups(prev => ({ ...prev, [group.id]: true }));
  };

  const cancelEditGroup = () => {
    setEditingGroup(null);
  };

  const saveEditGroup = () => {
    if (!editingGroup.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    
    const updated = modifierGroups.map(g => 
      g.id === editingGroup.id ? {
        ...editingGroup,
        options: editingGroup.options.filter(o => o.name.trim()).map(o => ({
          name: o.name.trim(),
          price: parseFloat(o.price) || 0
        }))
      } : g
    );
    
    setModifierGroups(updated);
    saveModifierGroups(updated);
    setEditingGroup(null);
  };

  const addOptionToNewGroup = () => {
    setNewGroup(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price: 0 }]
    }));
  };

  const updateNewGroupOption = (index, field, value) => {
    setNewGroup(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeNewGroupOption = (index) => {
    setNewGroup(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const toggleNewGroupCategory = (cat) => {
    setNewGroup(prev => ({
      ...prev,
      compatible_categories: prev.compatible_categories.includes(cat)
        ? prev.compatible_categories.filter(c => c !== cat)
        : [...prev.compatible_categories, cat]
    }));
  };

  const addOptionToEditGroup = () => {
    setEditingGroup(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price: 0 }]
    }));
  };

  const updateEditGroupOption = (index, field, value) => {
    setEditingGroup(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeEditGroupOption = (index) => {
    setEditingGroup(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const toggleEditGroupCategory = (cat) => {
    setEditingGroup(prev => ({
      ...prev,
      compatible_categories: (prev.compatible_categories || []).includes(cat)
        ? prev.compatible_categories.filter(c => c !== cat)
        : [...(prev.compatible_categories || []), cat]
    }));
  };

  // ============== ADD-ONS FUNCTIONS ==============
  
  const saveAddOns = async (items = addOns) => {
    setSaving(true);
    try {
      const cleanAddOns = items.map(addon => ({
        id: addon.id,
        name: addon.name,
        price: typeof addon.price === 'string' ? parseFloat(addon.price) : addon.price,
        category: addon.category || 'general',
        compatible_categories: Array.isArray(addon.compatible_categories) ? addon.compatible_categories : [],
        is_available: addon.is_available !== false
      }));
      
      const updatedSettings = {
        ...settings,
        global_add_ons: cleanAddOns
      };
      
      await api.put('/admin/settings', updatedSettings);
      await fetchSettings();
      toast.success('Add-ons saved!');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save add-ons');
    }
    setSaving(false);
  };

  const addNewAddOn = () => {
    if (!newAddOn.name.trim() || !newAddOn.price) {
      toast.error('Please fill in name and price');
      return;
    }
    
    const addon = {
      id: `addon-${Date.now()}`,
      name: newAddOn.name.trim(),
      price: parseFloat(newAddOn.price),
      category: newAddOn.category,
      compatible_categories: newAddOn.compatible_categories,
      is_available: newAddOn.is_available
    };
    
    const updated = [...addOns, addon];
    setAddOns(updated);
    saveAddOns(updated);
    setNewAddOn({ name: '', price: '', category: 'general', compatible_categories: [], is_available: true });
  };

  const removeAddOn = (id) => {
    const updated = addOns.filter(a => a.id !== id);
    setAddOns(updated);
    saveAddOns(updated);
  };

  const startEditAddon = (addon) => {
    setEditingAddon({
      ...addon,
      price: addon.price.toString()
    });
  };

  const cancelEditAddon = () => {
    setEditingAddon(null);
  };

  const saveEditAddon = () => {
    if (!editingAddon.name.trim() || !editingAddon.price) {
      toast.error('Please fill in name and price');
      return;
    }
    
    const updated = addOns.map(a => 
      a.id === editingAddon.id 
        ? {
            ...editingAddon,
            price: parseFloat(editingAddon.price),
            compatible_categories: editingAddon.compatible_categories || []
          }
        : a
    );
    
    setAddOns(updated);
    saveAddOns(updated);
    setEditingAddon(null);
  };

  const updateEditingAddon = (field, value) => {
    setEditingAddon(prev => ({ ...prev, [field]: value }));
  };

  const toggleEditCompatibleCategory = (cat) => {
    setEditingAddon(prev => ({
      ...prev,
      compatible_categories: (prev.compatible_categories || []).includes(cat)
        ? prev.compatible_categories.filter(c => c !== cat)
        : [...(prev.compatible_categories || []), cat]
    }));
  };

  const toggleAddOnAvailability = (id) => {
    const updated = addOns.map(a => 
      a.id === id ? { ...a, is_available: !a.is_available } : a
    );
    setAddOns(updated);
    saveAddOns(updated);
  };

  const toggleCompatibleCategory = (cat) => {
    setNewAddOn(prev => ({
      ...prev,
      compatible_categories: prev.compatible_categories.includes(cat)
        ? prev.compatible_categories.filter(c => c !== cat)
        : [...prev.compatible_categories, cat]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="customizations-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-foreground">Modifier Library & Add-Ons</h1>
          <p className="text-muted-foreground mt-1">Manage modifier groups (like Milk Type) and simple add-ons</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeSection === 'modifiers' ? 'default' : 'outline'}
          onClick={() => setActiveSection('modifiers')}
          className="rounded-full"
          data-testid="modifiers-tab-btn"
        >
          <Layers className="w-4 h-4 mr-2" />
          Modifier Groups ({modifierGroups.length})
        </Button>
        <Button
          variant={activeSection === 'addons' ? 'default' : 'outline'}
          onClick={() => setActiveSection('addons')}
          className="rounded-full"
          data-testid="addons-tab-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Simple Add-Ons ({addOns.length})
        </Button>
      </div>

      {/* ============== MODIFIER GROUPS SECTION ============== */}
      {activeSection === 'modifiers' && (
        <div className="space-y-6">
          {/* Quick Templates */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-5 border border-amber-200 dark:border-amber-800">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Coffee className="w-5 h-5 text-amber-600" />
              Quick Add Common Modifiers
            </h3>
            <div className="flex flex-wrap gap-2">
              {MODIFIER_TEMPLATES.map(template => (
                <Button
                  key={template.name}
                  variant="outline"
                  size="sm"
                  onClick={() => addTemplateGroup(template)}
                  className="rounded-full bg-white dark:bg-gray-800 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  disabled={modifierGroups.some(g => g.name.toLowerCase() === template.name.toLowerCase())}
                  data-testid={`template-${template.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {template.name}
                  {modifierGroups.some(g => g.name.toLowerCase() === template.name.toLowerCase()) && ' ✓'}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Create New Modifier Group */}
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
              <h2 className="font-heading text-xl mb-4 text-foreground">Create Modifier Group</h2>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Group Name</Label>
                  <Input
                    value={newGroup.name}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Milk Type, Sugar Level"
                    data-testid="new-group-name"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground">Options (with prices)</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addOptionToNewGroup}
                      className="h-8"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Option
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {newGroup.options.map((option, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          value={option.name}
                          onChange={(e) => updateNewGroupOption(idx, 'name', e.target.value)}
                          placeholder="Option name"
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          value={option.price}
                          onChange={(e) => updateNewGroupOption(idx, 'price', e.target.value)}
                          placeholder="$0.00"
                          className="w-24"
                        />
                        {newGroup.options.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeNewGroupOption(idx)}
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground">Applies to Menu Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {menuCategories.map(cat => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => toggleNewGroupCategory(cat.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                          newGroup.compatible_categories.includes(cat.value)
                            ? 'bg-primary text-white'
                            : 'bg-muted hover:bg-muted/80 text-foreground'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {newGroup.compatible_categories.length === 0 
                      ? 'Select categories or leave empty for all items'
                      : `Applies to: ${newGroup.compatible_categories.join(', ')}`
                    }
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new-group-required"
                    checked={newGroup.is_required}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, is_required: e.target.checked }))}
                    className="rounded"
                  />
                  <Label htmlFor="new-group-required" className="text-sm text-foreground cursor-pointer">
                    Required selection (customer must choose one)
                  </Label>
                </div>

                <Button 
                  onClick={addNewModifierGroup}
                  className="w-full rounded-full"
                  disabled={saving}
                  data-testid="create-group-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Modifier Group
                </Button>
              </div>
            </div>

            {/* Existing Modifier Groups */}
            <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
              <h2 className="font-heading text-xl mb-4 text-foreground">
                Active Modifier Groups ({modifierGroups.length})
              </h2>
              
              {modifierGroups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No modifier groups configured yet.</p>
                  <p className="text-sm mt-2">Use the quick templates above or create your own!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {modifierGroups.map(group => (
                    <div 
                      key={group.id}
                      className={`rounded-xl border ${
                        group.is_available !== false ? 'bg-muted border-transparent' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}
                      data-testid={`modifier-group-${group.id}`}
                    >
                      {/* Group Header */}
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer"
                        onClick={() => toggleGroupExpand(group.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Layers className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{group.name}</h3>
                            <p className="text-xs text-muted-foreground">
                              {group.options?.length || 0} options · 
                              {group.compatible_categories?.length > 0 
                                ? ` ${group.compatible_categories.join(', ')}`
                                : ' All categories'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditGroup(group);
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Delete "${group.name}" modifier group?`)) {
                                removeModifierGroup(group.id);
                              }
                            }}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {expandedGroups[group.id] ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Options */}
                      {expandedGroups[group.id] && (
                        <div className="px-4 pb-4 border-t border-border/50">
                          {editingGroup?.id === group.id ? (
                            /* Edit Mode */
                            <div className="pt-4 space-y-4">
                              <Input
                                value={editingGroup.name}
                                onChange={(e) => setEditingGroup(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Group Name"
                                className="font-semibold"
                              />
                              
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm">Options</Label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={addOptionToEditGroup}
                                    className="h-7 text-xs"
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add
                                  </Button>
                                </div>
                                {editingGroup.options.map((opt, idx) => (
                                  <div key={idx} className="flex gap-2 items-center">
                                    <Input
                                      value={opt.name}
                                      onChange={(e) => updateEditGroupOption(idx, 'name', e.target.value)}
                                      placeholder="Option"
                                      className="flex-1 h-9"
                                    />
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={opt.price}
                                      onChange={(e) => updateEditGroupOption(idx, 'price', e.target.value)}
                                      className="w-20 h-9"
                                    />
                                    {editingGroup.options.length > 1 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeEditGroupOption(idx)}
                                        className="h-9 w-9 p-0 text-red-500"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>

                              <div className="space-y-2">
                                <Label className="text-sm">Categories</Label>
                                <div className="flex flex-wrap gap-1">
                                  {menuCategories.map(cat => (
                                    <button
                                      key={cat.value}
                                      type="button"
                                      onClick={() => toggleEditGroupCategory(cat.value)}
                                      className={`px-2 py-1 rounded-full text-xs transition-all ${
                                        (editingGroup.compatible_categories || []).includes(cat.value)
                                          ? 'bg-primary text-white'
                                          : 'bg-muted text-foreground'
                                      }`}
                                    >
                                      {cat.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="flex gap-2 pt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={cancelEditGroup}
                                  className="flex-1"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={saveEditGroup}
                                  disabled={saving}
                                  className="flex-1"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <div className="pt-3 space-y-2">
                              {group.options?.map((opt, idx) => (
                                <div 
                                  key={idx}
                                  className="flex items-center justify-between py-2 px-3 bg-background/50 rounded-lg"
                                >
                                  <span className="text-sm text-foreground">{opt.name}</span>
                                  <span className={`text-sm font-medium ${
                                    opt.price > 0 ? 'text-primary' : 'text-muted-foreground'
                                  }`}>
                                    {opt.price > 0 ? `+$${opt.price.toFixed(2)}` : 'Free'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============== ADD-ONS SECTION ============== */}
      {activeSection === 'addons' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Add New Add-On */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
            <h2 className="font-heading text-xl mb-4 text-foreground">Add New Add-On</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Name</Label>
                  <Input
                    value={newAddOn.name}
                    onChange={(e) => setNewAddOn(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Extra Shot, Bacon"
                    data-testid="addon-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Price (FJD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newAddOn.price}
                    onChange={(e) => setNewAddOn(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                    data-testid="addon-price-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Category</Label>
                <Select 
                  value={newAddOn.category} 
                  onValueChange={(value) => setNewAddOn(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="addon-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {addOnCategories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Compatible Menu Categories (leave empty for all)</Label>
                <div className="flex flex-wrap gap-2">
                  {menuCategories.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => toggleCompatibleCategory(cat.value)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        newAddOn.compatible_categories.includes(cat.value)
                          ? 'bg-primary text-white'
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {newAddOn.compatible_categories.length === 0 
                    ? 'Available for all menu items'
                    : `Available for: ${newAddOn.compatible_categories.join(', ')}`
                  }
                </p>
              </div>

              <Button 
                onClick={addNewAddOn}
                className="w-full rounded-full"
                disabled={saving}
                data-testid="add-addon-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Add-On
              </Button>
            </div>
          </div>

          {/* Existing Add-Ons */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
            <h2 className="font-heading text-xl mb-4 text-foreground">
              Current Add-Ons ({addOns.length})
            </h2>
            
            {addOns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No add-ons configured yet.</p>
                <p className="text-sm mt-2">Add some using the form on the left!</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {addOns.map(addon => (
                  <div 
                    key={addon.id}
                    className={`p-4 rounded-xl border ${
                      addon.is_available ? 'bg-muted border-transparent' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    }`}
                    data-testid={`addon-item-${addon.id}`}
                  >
                    {editingAddon?.id === addon.id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={editingAddon.name}
                            onChange={(e) => updateEditingAddon('name', e.target.value)}
                            placeholder="Name"
                            className="h-9"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            value={editingAddon.price}
                            onChange={(e) => updateEditingAddon('price', e.target.value)}
                            placeholder="Price"
                            className="h-9"
                          />
                        </div>
                        
                        <Select 
                          value={editingAddon.category || 'general'} 
                          onValueChange={(value) => updateEditingAddon('category', value)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {addOnCategories.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <div className="flex flex-wrap gap-1">
                          {menuCategories.map(cat => (
                            <button
                              key={cat.value}
                              type="button"
                              onClick={() => toggleEditCompatibleCategory(cat.value)}
                              className={`px-2 py-1 rounded-full text-xs transition-all ${
                                (editingAddon.compatible_categories || []).includes(cat.value)
                                  ? 'bg-primary text-white'
                                  : 'bg-background text-foreground'
                              }`}
                            >
                              {cat.label}
                            </button>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelEditAddon}
                            className="flex-1 h-9"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveEditAddon}
                            disabled={saving}
                            className="flex-1 h-9"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleAddOnAvailability(addon.id)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                              addon.is_available 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                                : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            }`}
                            title={addon.is_available ? 'Click to disable' : 'Click to enable'}
                          >
                            {addon.is_available ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                          </button>
                          <div>
                            <div className="font-medium text-foreground">{addon.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {addon.category} · 
                              {addon.compatible_categories?.length > 0 
                                ? ` ${addon.compatible_categories.join(', ')}`
                                : ' All items'
                              }
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-primary">${addon.price.toFixed(2)}</span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditAddon(addon)}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm(`Delete "${addon.name}"?`)) {
                                  removeAddOn(addon.id);
                                }
                              }}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
